import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLoggedinUser, useService } from '@hooks';
import { assementService, notificationService, ParkArea, Permission, simulationService, Site, siteService } from '@services';
import { RxUtils, Tr } from '@utils';
import { Button, Stack } from '@mui/material';
import { EntityDeleteConfirmationDialog } from '@components/common';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { LocationViewDialog, LocationAddUpdateDialog, LocationList, LocationUpdateEnergyPriceDialog } from '@components/portal/locations';
import { ReactComponent as Illustration } from '@assets/illustrations/no-locations-added.svg';

const paddingX = 9;

export const Location: FC = () => {
	const user = useLoggedinUser();
	const navigate = useNavigate();
	const { t } = useTranslation('portal');
	const [searchText, setSearchText] = useState('');
	const [searchResult, setSearchResult] = useState<Site[]>([]);
	const [viewingEntity, setViewingEntity] = useState<Site>();
	const [editingEntity, setEditingEntity] = useState<Site>();
	const [deletingEntities, setDeletingEntities] = useState<Site[]>([]);
	const [selectedEntities, setSelectedEntities] = useState<Site[]>([]);
	const [deletingParkArea, setDeletingParkArea] = useState<ParkArea>();
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [priceUpdateModalOpen, setPriceUpdateModalOpen] = useState(false);
	const { data: latestSimulation } = useService(() => simulationService.getLatest());
	const { data: latestAssesment } = useService(() => assementService.latest());
	const { data: allEntities = [], trigger } = useService(() => siteService.getAll());
	const entities = (searchText ? searchResult : allEntities).sort((item1, item2) => item1.name.localeCompare(item2.name));

	const onAdd = (s: Partial<Site>) => {
		RxUtils.promisify(siteService.create(s), () => (trigger(), notificationService.sendSuccess(t('location.add-success-message'))));
	};
	const onEdit = (s: Partial<Site>) => {
		s.id &&
			RxUtils.promisify(
				siteService.update(s.id, s),
				() => (trigger(), notificationService.sendSuccess(t('location.update-success-message')))
			);
	};
	const onDelete = (s: Site) => {
		RxUtils.promisify(siteService.delete(s), () => (trigger(), notificationService.sendSuccess(t('location.delete-success-message'))));
	};
	const onDeleteMultiple = (s: Site[]) => {
		RxUtils.promisify(
			siteService.deleteMultiple(s),
			() => (trigger(), notificationService.sendSuccess(t('location.delete-multiple-success-message')))
		);
	};
	const onPriceUpdate = (sites: Site[], price: Partial<Site>) => {
		RxUtils.promisify(
			siteService.updateBulk(sites.map((site) => new Site({ ...site, ...price }))),
			() => (trigger(), notificationService.sendSuccess(t('location.update-energy-price-success-message')))
		);
	};
	const onParkingAreaDelete = (item: ParkArea) => {
		item.siteId &&
			RxUtils.promisify(
				siteService.parkArea(item.siteId).delete(item),
				() => (trigger(), notificationService.sendSuccess(t('parking-area.delete-success-message')))
			);
	};

	useEffect(() => {
		if (searchText) {
			RxUtils.promisify(siteService.search(searchText), setSearchResult);
		} else {
			setSearchResult([]);
		}
	}, [searchText]);

	return (
		<>
			{Array.isArray(allEntities) && allEntities.length ? (
				<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
					<PortalPageTitle
						paddingX={paddingX}
						title={<Tr.Portal path="location.title" />}
						subtitle={<Tr.Portal path="location.subtitle" />}
						displaySearchbox
						searchboxProps={{
							testId: 'location-search-box',
							placeholder: t('location.search-placeholder'),
							searchText,
							onSearchTextChange: setSearchText
						}}
					/>
					<LocationList
						showResultCount={!!searchText}
						paddingX={paddingX}
						entities={entities}
						selected={selectedEntities}
						latestAssesment={latestAssesment}
						simulationId={latestSimulation?.id}
						onSelectChange={setSelectedEntities}
						onView={setViewingEntity}
						onEdit={setEditingEntity}
						onDelete={(entity) => setDeletingEntities([entity])}
						onParkAreaDelete={setDeletingParkArea}
						onParkAreaEdit={({ id, siteId }) => navigate(`./${siteId}/parking-area/${id}/edit`)}
					/>
					{user?.hasPermission(Permission.SITE_WRITE) && (
						<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
							{selectedEntities?.length ? (
								<>
									<Button
										data-testid="delete-location-modal-open-button"
										variant="contained"
										color="secondary"
										sx={{ textTransform: 'uppercase' }}
										onClick={() => setDeletingEntities(selectedEntities)}
									>
										<Tr.Portal path="delete" />
									</Button>
									<Button
										data-testid="update-location-energy-prices-modal-open-button"
										variant="outlined"
										color="secondary"
										sx={{ textTransform: 'uppercase' }}
										onClick={() => setPriceUpdateModalOpen(true)}
									>
										<Tr.Portal path="location.update-energy-price" />
									</Button>
									<Button
										data-testid="cancel-location-selection-button"
										variant="outlined"
										color="secondary"
										sx={{ textTransform: 'uppercase' }}
										onClick={() => setSelectedEntities([])}
									>
										<Tr.Portal path="cancel" />
									</Button>
								</>
							) : (
								<>
									<Button
										data-testid="add-park-area-navigation-button"
										variant="contained"
										color="secondary"
										onClick={() => navigate(`./parking-area/add`)}
									>
										<Tr.Portal path="parking-area.add-parking-area" />
									</Button>
									<Button
										data-testid="add-location-modal-open-button"
										variant="contained"
										sx={{ textTransform: 'uppercase' }}
										onClick={() => setAddModalOpen(true)}
									>
										<Tr.Portal path="location.add-location" />
									</Button>
								</>
							)}
						</Stack>
					)}
				</Stack>
			) : (
				<EmptyEntityPageLayout
					illustration={<Illustration />}
					title={<Tr.Portal path="location.no-locations-added-title" />}
					addButtonText={<Tr.Portal path="location.add-location" />}
					onAddButtonClick={() => setAddModalOpen(true)}
					addButtonProps={{ 'data-testid': 'add-location-modal-open-button' }}
				/>
			)}
			<LocationViewDialog open={!!viewingEntity} entity={viewingEntity} onClose={() => setViewingEntity(undefined)} />
			<LocationAddUpdateDialog
				open={addModalOpen}
				onClose={() => setAddModalOpen(false)}
				onPrimaryButtonClick={(s) => (setAddModalOpen(false), onAdd?.(s))}
			/>
			<LocationAddUpdateDialog
				open={!!editingEntity}
				entity={editingEntity}
				onClose={() => setEditingEntity(undefined)}
				onPrimaryButtonClick={(s) => (setEditingEntity(undefined), onEdit?.(s))}
			/>
			<LocationUpdateEnergyPriceDialog
				open={priceUpdateModalOpen}
				onClose={() => setPriceUpdateModalOpen(false)}
				onUpdate={(prices) => (setPriceUpdateModalOpen(false), onPriceUpdate(selectedEntities, prices), setSelectedEntities([]))}
			/>
			<EntityDeleteConfirmationDialog
				open={!!deletingEntities?.length}
				content={<Tr.Portal path="location.delete-confirmation-message" />}
				buttonProps={{ 'data-testid': 'location-delete-confirmation-button' }}
				onClose={() => setDeletingEntities([])}
				onAccept={() => {
					deletingEntities.length > 1 ? onDeleteMultiple?.(deletingEntities) : onDelete?.(deletingEntities[0]);
					setDeletingEntities([]);
					setSelectedEntities([]);
				}}
			>
				{deletingEntities.map((e) => `"${e.name}"`).join(', ')}
			</EntityDeleteConfirmationDialog>
			<EntityDeleteConfirmationDialog
				open={!!deletingParkArea}
				content={<Tr.Portal path="parking-area.delete-confirmation-message" />}
				buttonProps={{ 'data-testid': 'parking-area-delete-confirmation-button' }}
				onClose={() => setDeletingParkArea(undefined)}
				onAccept={() => {
					deletingParkArea && onParkingAreaDelete?.(deletingParkArea);
					setDeletingParkArea(undefined);
				}}
			>
				{deletingParkArea?.name}
			</EntityDeleteConfirmationDialog>
		</>
	);
};
